import { useState } from "react";
import { motion } from "framer-motion";
import {
  HiOutlineTicket,
  HiOutlineCheckCircle,
  HiOutlineEye,
  HiOutlineClock,
  HiOutlineClipboard,
  HiOutlineClipboardCheck,
} from "react-icons/hi";
import { HiOutlineCalendar } from "react-icons/hi2";
import { IoHomeOutline } from "react-icons/io5";
import moment from "moment-jalaali";
import Pagination from "components/table/Pagination";
import { LicenseDTO, LicenseScope } from "types/assessmentTypes";
import IconButton from "components/button/IconButton";
import { IoMdRefresh } from "react-icons/io";
import { useTranslation } from "react-i18next";

export type FilterStatus = "ALL" | "ACTIVE" | "DEACTIVE";
export type FilterGroupType = "ALL" | "MICRO" | "SMALL" | "MEDIUM" | "LARGE";

export interface FilterState {
  status: FilterStatus;
  groupType: FilterGroupType;
}

interface LicenseListProps {
  licenses: LicenseDTO[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onFilterChange: (filters: FilterState) => void;
  onRefresh?: () => void;
  companies?: Array<{ id: number; name: string }>;
}

const LicenseList = ({
  licenses,
  totalItems,
  totalPages,
  currentPage,
  isLoading,
  onPageChange,
  onFilterChange,
  onRefresh,
  companies = [],
}: LicenseListProps) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [filters, setFilters] = useState<FilterState>({
    status: "ALL",
    groupType: "ALL",
  });
  const [copiedKey, setCopiedKey] = useState<number | null>(null);

  const formatDate = (date: string) => {
    return moment(date).format(lang === "fa" ? "jYYYY/jMM/jDD" : "YYYY/MM/DD");
  };

  const getStatusBadge = (license: LicenseDTO) => {
    const isUsed = !!license.usedAt;

    if (isUsed) {
      return {
        color:
          "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
        icon: <HiOutlineEye className="h-4 w-4" />,
        text: t("licenses.used"),
      };
    }

    return {
      color:
        "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400",
      icon: <HiOutlineCheckCircle className="h-4 w-4" />,
      text: t("licenses.unused"),
    };
  };

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const groupTypeOptions = [
    { value: "ALL", label: t("licenses.allTypes") },
    { value: "MICRO", label: t("group.types.MICRO") },
    { value: "SMALL", label: t("group.types.SMALL") },
    { value: "MEDIUM", label: t("group.types.MEDIUM") },
    { value: "LARGE", label: t("group.types.LARGE") },
  ];

  const copyToClipboard = (text: string, id: number) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopiedKey(id);
          setTimeout(() => setCopiedKey(null), 2000);
        })
        .catch(() => {
          const textArea = document.createElement("textarea");
          textArea.value = text;
          textArea.style.position = "fixed";
          textArea.style.left = "-999999px";
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
          setCopiedKey(id);
          setTimeout(() => setCopiedKey(null), 2000);
        });
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedKey(id);
      setTimeout(() => setCopiedKey(null), 2000);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="loader w-6 text-brand-500" />
      </div>
    );
  }

  return (
    <div>
      {/* Header with filters */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white">
            {t("licenses.myLicensesList")}
          </h2>
          <IconButton
            handler={onRefresh}
            title={t("buttons.refresh")}
            icon={<IoMdRefresh size={22} />}
          />
          <span className="rounded-full bg-gray-200/80 px-3 py-1 text-sm font-medium text-gray-700 dark:bg-gray-700/80 dark:text-gray-300">
            {t("licenses.licenseTotal")} : {totalItems}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Status Filters */}
          <div className="flex items-center rounded-xl bg-gray-100/80 p-1 backdrop-blur-sm dark:bg-gray-800/80">
            <button
              onClick={() => handleFilterChange("status", "ALL")}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                filters.status === "ALL"
                  ? "bg-white text-gray-900 shadow-md ring-1 ring-gray-200 dark:bg-gray-700 dark:text-white dark:ring-gray-600"
                  : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              {t("licenses.all")}
            </button>
            <button
              onClick={() => handleFilterChange("status", "ACTIVE")}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                filters.status === "ACTIVE"
                  ? "bg-white text-gray-900 shadow-md ring-1 ring-gray-200 dark:bg-gray-700 dark:text-white dark:ring-gray-600"
                  : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              <span className="flex items-center gap-1">
                <HiOutlineCheckCircle
                  className={`h-3.5 w-3.5 ${
                    filters.status === "ACTIVE"
                      ? "text-gray-700 dark:text-gray-300"
                      : "text-gray-500 dark:text-gray-500"
                  }`}
                />
                {t("licenses.unused")}
              </span>
            </button>
            <button
              onClick={() => handleFilterChange("status", "DEACTIVE")}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                filters.status === "DEACTIVE"
                  ? "bg-white text-gray-900 shadow-md ring-1 ring-gray-200 dark:bg-gray-700 dark:text-white dark:ring-gray-600"
                  : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              <span className="flex items-center gap-1">
                <HiOutlineEye
                  className={`h-3.5 w-3.5 ${
                    filters.status === "DEACTIVE"
                      ? "text-gray-700 dark:text-gray-300"
                      : "text-gray-500 dark:text-gray-500"
                  }`}
                />
                {t("licenses.used")}
              </span>
            </button>
          </div>

          {/* Group Type Filter - Select */}
          <div className="relative">
            <select
              value={filters.groupType}
              onChange={(e) => handleFilterChange("groupType", e.target.value)}
              className="appearance-none rounded-xl bg-gray-100/80 px-4 py-1.5 pr-8 text-xs font-medium text-gray-700 transition-all duration-200 hover:bg-gray-200/80 focus:outline-none focus:ring-2 focus:ring-brand-500/50 dark:bg-gray-800/80 dark:text-gray-300 dark:hover:bg-gray-700/80"
            >
              {groupTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
              <svg
                className="h-4 w-4 text-gray-500 dark:text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {licenses.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-2xl bg-white p-12 text-center shadow-lg dark:bg-navy-800"
        >
          <div className="mb-4 text-7xl">🎫</div>
          <h3 className="mb-2 text-xl font-bold text-gray-800 dark:text-white">
            {t("licenses.noLicenses")}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {t("licenses.buyFirstLicense")}
          </p>
        </motion.div>
      )}

      {/* License Cards Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {licenses.map((license, index) => {
          const statusBadge = getStatusBadge(license);

          return (
            <motion.div
              key={license.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ y: -4 }}
              className="group relative overflow-hidden rounded-2xl bg-white shadow-lg transition-all duration-300 hover:shadow-xl dark:bg-navy-800"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-brand-500 to-brand-600 px-4 py-3 text-white dark:from-brand-700 dark:to-brand-800">
                <div className="flex items-start justify-between">
                  <div className="text-xs opacity-90">
                    {t("licenses.licenseKey")}
                  </div>
                  <HiOutlineTicket className="h-6 w-6 opacity-80" />
                </div>
                <div className="mt-1 flex items-center justify-between gap-4">
                  <span className="font-mono text-sm font-bold tracking-wider">
                    {license.licenseKey}
                  </span>
                  <button
                    onClick={() =>
                      copyToClipboard(license.licenseKey, license.id)
                    }
                    disabled={license.used}
                    className="rounded p-1 transition-colors hover:bg-white/20"
                  >
                    {copiedKey === license.id ? (
                      <HiOutlineClipboardCheck className="h-5 w-5 text-white" />
                    ) : (
                      <HiOutlineClipboard className="h-5 w-5 text-white/80" />
                    )}
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <span
                      className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${statusBadge.color}`}
                    >
                      {statusBadge.icon}
                      {statusBadge.text}
                    </span>
                    <span className="mr-2 flex items-center gap-1 text-xs text-gray-800 dark:text-gray-400">
                      <HiOutlineCalendar className="h-3 w-3" />
                      {license.scope === LicenseScope.ORGANIZATION
                        ? license.organization?.name || "-"
                        : t(`group.types.${license.groupType}`)}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="flex items-center gap-1 text-xs text-gray-800 dark:text-gray-400">
                      <HiOutlineCalendar className="h-3 w-3" />
                      {formatDate(license.createdAt)}
                    </span>

                    <span className="flex items-center gap-1 text-xs text-gray-800 dark:text-gray-400">
                      <IoHomeOutline className="h-3 w-3" />
                      {license.scope === LicenseScope.ORGANIZATION
                        ? t("licenses.scopes.ORGANIZATION")
                        : t("licenses.scopes.COMPANY")}
                    </span>
                  </div>
                </div>

                {license.used && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-gray-700 dark:text-gray-400">
                    <HiOutlineClock className="h-3 w-3" />
                    {t("licenses.usedAt")} : {formatDate(license.usedAt)} (
                    {license.company?.name || "-"} )
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default LicenseList;
